import { BreadcrumbItem, Breadcrumbs } from "@vds/breadcrumbs";
import { Button, ButtonGroup } from "@vds/buttons";
import { Link } from "react-router-dom";
import {
  editRoles,
  saveRoles,
  deleteRolePermissions,
  updateRoleData,
  deleteRole,
  getRoleTableData,
} from "../../model/roleService";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { Input } from "@vds/inputs";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { InlineCheckbox } from "../../global/utility/styledComponents";
import { CustomTable } from "../../global/components/Tables/CustomTable";
import { useSearchParams } from "react-router-dom";
import {
  RoleContainer,
  AddRoleContainerWithButtons,
  AddRoleHeading,
  SaveButton,
  BreadcrumbContainer,
  InputContainer,
  RoleInputWrapper,
} from "./Styles";
import { hasPermission } from "../../global/utility/permissionUtils";
import NotificationBanner from "../../layouts/NotificationBanner";
import { showNotificationBanner } from "../../store/actions";

function EditRoleTemplate() {
  const { activeSession } = useSelector(
    (state) => state.sessionReducer.activeSession
  );
  const [searchParams] = useSearchParams();
  const roleId = searchParams.get('roleId');
  const resourceIdentifier = { id: roleId };
  const [roleEdit, setRoleEdit] = useState(null);
  const [dataList, setDataList] = useState([]);
  const [columnData, setCoulumnData] = useState([]);
  const [initialData, setInitialData] = useState([]);
  const [description, setDescription] = useState("");
  const [originalDescription, setOriginalDescription] = useState("");
  const [tableLoading, setTableLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const excludedRoles = ["Admin", "Site Manager", "Site Viewer"]; // Temporarily excluding the delete button for these roles
  const [showNotification, setShowNotification] = useState(false);

  const editTemplate = async () => {
    try {
      const editRole = await editRoles(activeSession, resourceIdentifier.id);
      transformData(editRole);
      if (editRole) {
        setRoleEdit(editRole || null);
        setDescription(editRole.description || "");
        setOriginalDescription(editRole.description || "");
        setTableLoading(false);
      } else {
        setRoleEdit("");
        setTableLoading(true);
      }
    } catch (error) {
      console.log("Getting error", error);
      setTableLoading(false);
    }
  };

  const transformData = (data) => {
    if (!data || !Array.isArray(data.requestdefinitions)) {
      return;
    }
    // Filter out requestdefinitions where isSystemOnly is true
    const filteredRequestDefinitions = data.requestdefinitions.filter(
      (definition) => definition && !definition.isSystemOnly
    );
    const transformedData = getRoleTableData(filteredRequestDefinitions);
    setDataList(transformedData.tableDataList);
    setInitialData(transformedData.tableDataList);
    setCoulumnData(getColumnData(transformedData.uniqueActions));
  };

  const handlePermissionChange = (object, action) => {
    setDataList((prevDataList) =>
      prevDataList.map((item) =>
        item.object === object
          ? {
              ...item,
              [action]: {
                isenabled: !item[action].isenabled,
                ismutable: item[action].ismutable,
              },
            }
          : item
      )
    );
  };

  const savePermissions = async () => {
    const hrefsToRemove = [];
    const hrefsToAdd = [];

    const roleInfo = {
      name: roleEdit.name,
      description: description,
    };
    dataList.forEach(({ object, ...actions }) => {
      const initialItem = initialData.find((item) => item.object === object);
      if (!initialItem) return;
      Object.keys(actions).forEach((action) => {
        if (action === "key") return;
        const initialIsSelected = initialItem[action].isenabled;
        if (actions[action].isenabled && !initialIsSelected) {
          hrefsToAdd.push(
            `#/${object}/${action.charAt(0).toUpperCase() + action.slice(1)}`
          );
        }
        if (!actions[action].isenabled && initialIsSelected) {
          hrefsToRemove.push(
            `#/${object}/${action.charAt(0).toUpperCase() + action.slice(1)}`
          );
        }
      });
    });

    try {
      setTableLoading(true);
      if (hrefsToAdd.length > 0) {
        await saveRoles(activeSession, resourceIdentifier, hrefsToAdd);
      }
      if (hrefsToRemove.length > 0) {
        await deleteRolePermissions(
          activeSession,
          resourceIdentifier,
          hrefsToRemove
        );
      }
      if (description !== originalDescription) {
        await updateRoleData(activeSession, resourceIdentifier, roleInfo);
      }
      const notificationInfo = {
        type: "Role update success",
        priority: "success",
        showNotification: true,
      };
      dispatch(showNotificationBanner(notificationInfo));
      navigate("/roles");
    } catch (error) {
      setTableLoading(false);
      return null;
    }
  };

  const handleDeleteRole = async (resourceIdentifier) => {
    if (!hasPermission("Role management", "Delete")) {
      showPermissionsBanner();
    } else {
      try {
        setTableLoading(true);
        await deleteRole(activeSession, resourceIdentifier);
        navigate("/roles");
      } catch (error) {
        console.log("Getting error", error);
        setTableLoading(false);
      }
    }
  };

  const isButtonEnabled =
    JSON.stringify(dataList) !== JSON.stringify(initialData) ||
    description !== originalDescription;

  const getColumnData = (uniqueActions) => {
    let column = [
      {
        title: " ",
        dataIndex: "object",
        key: "object",
      },
    ];

    uniqueActions.forEach((action) => {
      column.push({
        title: action,
        dataIndex: action,
        key: action,
        render: (text, record) => {
          if (record[action]) {
            return (
              <InlineCheckbox
                selected={record[action].isenabled}
                onChange={() => handlePermissionChange(record.object, action)}
                disabled={!record[action].ismutable}
              />
            );
          } else {
            return <> - </>;
          }
        },
      });
    });
    return column;
  };

  useEffect(() => {
    if (resourceIdentifier.id) {
      setTableLoading(true);
      editTemplate();
    }
  }, [activeSession, resourceIdentifier.id]);

  const showPermissionsBanner = () => {
    const notificationInfo = {
      type: "No Permission",
      priority: "critical",
      showNotification: true,
    };
    dispatch(showNotificationBanner(notificationInfo));
  };

  useEffect(() => {
    if (!roleId) {
      console.error('Missing roleId parameter');
      navigate('/roles');
    }
  }, [roleId, navigate]);

  return (
    <>
      <BreadcrumbContainer>
        <Breadcrumbs surface="light">
          <BreadcrumbItem>
            <Link
              data-testid="editRoleTemplate_settingsLink"
              to="/settings"
              className="breadcrums-item_link"
            >
              Settings
            </Link>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <Link
              data-testid="editRoleTemplate_rolesLink"
              to="/roles"
              className="breadcrums-item_link"
            >
              Roles
            </Link>
          </BreadcrumbItem>
          <BreadcrumbItem>{roleEdit?.name}</BreadcrumbItem>
        </Breadcrumbs>
      </BreadcrumbContainer>
      <br />

      <RoleContainer>
        <AddRoleContainerWithButtons>
          <AddRoleHeading>{roleEdit?.name}</AddRoleHeading>
          <SaveButton>
            <ButtonGroup
              rowQuantity={{ mobile: 1, tablet: 2, desktop: 2 }}
              data={[
                ...(roleEdit
                  ? [
                      {
                        children: "Save role",
                        size: "small",
                        use: "primary",
                        width: "auto",
                        "data-testid": "editRole_saveRoleButton",
                        onClick: () => {
                          if (!hasPermission("Role management", "Edit")) {
                            showPermissionsBanner();
                          } else {
                            savePermissions();
                          }
                        },
                        disabled: !isButtonEnabled,
                      },
                    ]
                  : []),

                // Temporarily Conditionally adding the "Delete a role" button for custom Role
                ...(roleEdit && !excludedRoles.includes(roleEdit?.name)
                  ? [
                      {
                        children: "Delete role",
                        size: "small",
                        use: "secondary",
                        width: "auto",
                        "data-testid": "editRole_deleteRoleButton",
                        onClick: () => handleDeleteRole(resourceIdentifier),
                      },
                    ]
                  : []),
              ]}
            />
          </SaveButton>
        </AddRoleContainerWithButtons>
        <InputContainer>
          <RoleInputWrapper className="full-width-input">
            <Input
              type="text"
              label="Description"
              readOnly={false}
              required={true}
              disabled={false}
              error={false}
              errorText="Enter a valid description."
              value={description}
              placeholder="Enter description"
              onChange={(e) => setDescription(e.target.value)}
            />
          </RoleInputWrapper>
        </InputContainer>
      </RoleContainer>
      <CustomTable
        dataList={dataList}
        tableLoading={tableLoading}
        pagination={true}
        renderedColumns={columnData}
      />
    </>
  );
}

export default EditRoleTemplate;
